/**
 * Toll-Free Verification Submission Dialog
 *
 * Single-step form that captures the carrier-required fields for a Telnyx
 * Toll-Free Verification (TFV) submission. Mirrors the visual language of
 * ProvisionNumberDialog — single dialog, sectioned form, slate-900 primary CTA.
 *
 * Required by US carriers for production-grade toll-free SMS delivery. Without
 * an approved TFV, toll-free traffic is heavily throttled or filtered.
 */
import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@respectly/ui';
import {
  ScrollText,
  Loader2,
  AlertCircle,
  ShieldCheck,
  User,
  MessageSquare,
  ListChecks,
  Info,
} from 'lucide-react';
import type {
  TelnyxStatusResponse,
  TelnyxSubmitTfvRequest,
  TelnyxTfvUseCase,
  TelnyxTfvVolume,
} from '@respectly/shared-types';

interface TfvSubmissionDialogProps {
  open: boolean;
  onClose: () => void;
  status: TelnyxStatusResponse;
  isSubmitting: boolean;
  onSubmit: (params: TelnyxSubmitTfvRequest) => Promise<boolean>;
}

const USE_CASE_OPTIONS: Array<{ value: TelnyxTfvUseCase; label: string }> = [
  { value: 'MIXED', label: 'Mixed (general business messaging)' },
  { value: 'CUSTOMER_CARE', label: 'Customer Care' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: '2FA', label: 'Two-Factor Auth (2FA)' },
  { value: 'APP_NOTIFICATIONS', label: 'App Notifications' },
  { value: 'DELIVERY_NOTIFICATIONS', label: 'Delivery Notifications' },
  { value: 'FRAUD_ALERT', label: 'Fraud Alert' },
  { value: 'SECURITY_ALERT', label: 'Security Alert' },
  { value: 'HIGHER_EDUCATION', label: 'Higher Education' },
  { value: 'POLLING_VOTING', label: 'Polling / Voting' },
  { value: 'PUBLIC_SERVICE_ANNOUNCEMENT', label: 'Public Service Announcement' },
];

const VOLUME_OPTIONS: TelnyxTfvVolume[] = [
  '10',
  '100',
  '1,000',
  '10,000',
  '100,000',
  '250,000',
  '500,000',
  '750,000',
  '1,000,000',
  '5,000,000',
  '10,000,000+',
];

const DEFAULT_USE_CASE_SUMMARY =
  'Business messaging via Respectly — appointment reminders, account notifications, two-way customer support, and consented marketing.';
const DEFAULT_PRODUCTION_MESSAGE =
  'Hi {{first_name}}, this is a reminder about your appointment tomorrow at 10am. Reply STOP to opt out.';
const DEFAULT_OPT_IN_DESCRIPTION =
  'Customers opt in via web form, in-person consent capture, or import with documented prior express consent. Each message includes a way to opt out (STOP).';

function formatE164(num: string): string {
  const m = num.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `+1 (${m[1]}) ${m[2]}-${m[3]}`;
  return num;
}

export function TfvSubmissionDialog({
  open,
  onClose,
  status,
  isSubmitting,
  onSubmit,
}: TfvSubmissionDialogProps) {
  const tollFreeNumbers = status.numbers.filter((n) => n.type === 'tollfree' && n.isActive);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [useCase, setUseCase] = useState<TelnyxTfvUseCase>('MIXED');
  const [volume, setVolume] = useState<TelnyxTfvVolume>('10,000');
  const [useCaseSummary, setUseCaseSummary] = useState(DEFAULT_USE_CASE_SUMMARY);
  const [productionMessageContent, setProductionMessageContent] = useState(
    DEFAULT_PRODUCTION_MESSAGE,
  );
  const [optInWorkflowDescription, setOptInWorkflowDescription] = useState(
    DEFAULT_OPT_IN_DESCRIPTION,
  );
  const [optInImageUrlsRaw, setOptInImageUrlsRaw] = useState('');
  const [additionalInformation, setAdditionalInformation] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSubmitError(null);
    }
  }, [open]);

  const handleClose = () => {
    setSubmitError(null);
    onClose();
  };

  const validate = (): string | null => {
    if (!firstName.trim()) return 'Contact first name is required';
    if (!lastName.trim()) return 'Contact last name is required';
    if (!email.trim() || !/.+@.+\..+/.test(email)) return 'A valid contact email is required';
    if (!phone.trim()) return 'Contact phone is required';
    if (!useCaseSummary.trim()) return 'Use-case summary is required';
    if (!productionMessageContent.trim())
      return 'A representative production message is required';
    if (!optInWorkflowDescription.trim()) return 'Opt-in workflow description is required';
    if (tollFreeNumbers.length === 0)
      return 'No active toll-free numbers to verify — provision one first.';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError(null);

    const optInWorkflowImageUrls = optInImageUrlsRaw
      .split(/\r?\n/)
      .map((u) => u.trim())
      .filter((u) => u.length > 0);

    const ok = await onSubmit({
      contact: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
      },
      useCase,
      volume,
      useCaseSummary: useCaseSummary.trim(),
      productionMessageContent: productionMessageContent.trim(),
      optInWorkflowDescription: optInWorkflowDescription.trim(),
      optInWorkflowImageUrls:
        optInWorkflowImageUrls.length > 0 ? optInWorkflowImageUrls : undefined,
      additionalInformation: additionalInformation.trim() || undefined,
    });

    if (ok) handleClose();
  };

  const readyMissing: string[] = [];
  if (!status.tollFreeIdentityComplete)
    readyMissing.push('Business website + full billing address (Overview tab)');
  if (!status.messagingProfile) readyMissing.push('Messaging profile');
  if (tollFreeNumbers.length === 0) readyMissing.push('At least one active toll-free number');

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
              Toll-Free Verification
            </p>
            <DialogTitle className="mt-0.5">Submit verification request</DialogTitle>
          </div>
        </DialogHeader>

        {/* Pre-flight warnings */}
        {readyMissing.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-300">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div>
                <p className="font-semibold">Not ready to submit</p>
                <ul className="mt-1 space-y-0.5">
                  {readyMissing.map((m) => (
                    <li key={m}>· {m}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Numbers being verified */}
        {tollFreeNumbers.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 dark:border-slate-800 dark:from-slate-900/40 dark:to-slate-950/40">
            <div className="flex items-baseline justify-between">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                Numbers in this submission
              </p>
              <span className="text-[11px] text-slate-500">
                {tollFreeNumbers.length} {tollFreeNumbers.length === 1 ? 'number' : 'numbers'}
              </span>
            </div>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {tollFreeNumbers.map((n) => (
                <li
                  key={n.id}
                  className="inline-flex items-center rounded-md bg-white px-2 py-1 font-mono text-[12px] tabular-nums text-slate-800 ring-1 ring-slate-200/70 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700/70"
                >
                  {formatE164(n.number)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Sections */}
        <div className="space-y-6">
          <Section icon={User} title="Business contact">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FieldText
                label="First name"
                value={firstName}
                onChange={setFirstName}
                placeholder="Jamie"
                required
              />
              <FieldText
                label="Last name"
                value={lastName}
                onChange={setLastName}
                placeholder="Rivera"
                required
              />
              <FieldText
                label="Email"
                value={email}
                onChange={setEmail}
                placeholder="ops@example.com"
                type="email"
                required
              />
              <FieldText
                label="Phone"
                value={phone}
                onChange={setPhone}
                placeholder="+1 555 123 4567"
                type="tel"
                required
              />
            </div>
          </Section>

          <Section icon={ShieldCheck} title="Use case & volume">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Use case
                </Label>
                <Select
                  value={useCase}
                  onValueChange={(v: TelnyxTfvUseCase) => setUseCase(v)}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {USE_CASE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Estimated monthly volume
                </Label>
                <Select
                  value={volume}
                  onValueChange={(v: TelnyxTfvVolume) => setVolume(v)}
                >
                  <SelectTrigger className="h-10 font-mono tabular-nums">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VOLUME_OPTIONS.map((v) => (
                      <SelectItem key={v} value={v} className="font-mono tabular-nums">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <FieldTextarea
              label="Use-case summary"
              value={useCaseSummary}
              onChange={setUseCaseSummary}
              rows={3}
              required
              hint="One paragraph the carrier reviewer will read first."
            />
          </Section>

          <Section icon={MessageSquare} title="Sample message">
            <FieldTextarea
              label="Production message content"
              value={productionMessageContent}
              onChange={setProductionMessageContent}
              rows={3}
              required
              hint="A real, representative outbound SMS. Include opt-out language (e.g. STOP)."
            />
          </Section>

          <Section icon={ListChecks} title="Opt-in proof">
            <FieldTextarea
              label="Opt-in workflow description"
              value={optInWorkflowDescription}
              onChange={setOptInWorkflowDescription}
              rows={3}
              required
            />
            <FieldTextarea
              label="Opt-in screenshot URLs"
              value={optInImageUrlsRaw}
              onChange={setOptInImageUrlsRaw}
              rows={3}
              placeholder={'https://respectly.ai/proof/optin1.png\nhttps://respectly.ai/proof/optin2.png'}
              hint="One URL per line. Carrier reviewers want to see your opt-in form / consent capture."
            />
          </Section>

          <Section icon={Info} title="Additional information" optional>
            <FieldTextarea
              label="Anything else the carrier should know"
              value={additionalInformation}
              onChange={setAdditionalInformation}
              rows={2}
            />
          </Section>
        </div>

        {submitError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/30 dark:text-rose-300">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{submitError}</span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || readyMissing.length > 0}
            className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            {isSubmitting ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <ScrollText className="mr-1.5 h-4 w-4" />
            )}
            {isSubmitting ? 'Submitting...' : 'Submit verification'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Form primitives ─────────────────────────────────────────────────────────

function Section({
  icon: Icon,
  title,
  optional,
  children,
}: {
  icon: typeof User;
  title: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-100 text-slate-600 ring-1 ring-slate-200/70 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700/70">
          <Icon className="h-3.5 w-3.5" />
        </div>
        <h4 className="text-[13px] font-semibold text-slate-900 dark:text-white">{title}</h4>
        {optional && (
          <span className="text-[10px] uppercase tracking-wider text-slate-400">optional</span>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function FieldText({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel';
  required?: boolean;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
        {required && <span className="ml-0.5 text-rose-500">*</span>}
      </Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10"
      />
    </div>
  );
}

function FieldTextarea({
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
  required,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
        {required && <span className="ml-0.5 text-rose-500">*</span>}
      </Label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className={cn(
          'flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm',
          'placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus-visible:ring-slate-500',
        )}
      />
      {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}
